/**
 * Force la saisie en majuscules dans tous les champs texte de l'application.
 *
 * Pourquoi un intercepteur global plutôt qu'une modification champ par champ : les saisies sont
 * réparties entre 84 `Form.Control` et 109 `<input>` bruts, sans composant commun. Traiter les
 * 193 emplacements un par un multiplierait les occasions d'en oublier un, et chaque nouveau
 * champ ajouté ensuite repartirait en minuscules.
 *
 * Ce n'est PAS un simple `text-transform: uppercase` : cette propriété CSS ne change que
 * l'affichage, la valeur envoyée au serveur resterait telle que tapée. Ici la valeur elle-même
 * est transformée, donc ce qui est enregistré est bien en majuscules.
 *
 * Types exclus, et pourquoi : un mot de passe est sensible à la casse — le forcer en majuscules
 * rendrait la connexion impossible. Une adresse e-mail se compare en minuscules côté serveur,
 * la majusculiser casserait la correspondance. Les autres types exclus (nombres, dates, cases à
 * cocher…) n'ont pas de casse ; les exclure évite un traitement inutile sur chaque frappe.
 *
 * Échappatoire ponctuelle : `data-sans-majuscules` sur un champ le laisse intact.
 */

const TYPES_EXCLUS = new Set([
    'password', 'email', 'number', 'date', 'time', 'datetime-local', 'month', 'week',
    'tel', 'url', 'checkbox', 'radio', 'file', 'submit', 'button', 'reset',
    'color', 'range', 'hidden', 'image',
]);

/**
 * Installe l'interception. Retourne la fonction de désinstallation (utile en test, et pour
 * respecter le contrat d'un effet React).
 */
export function activerMajusculesGlobales(): () => void {
    const surSaisie = (evenement: Event) => {
        const cible = evenement.target;
        if (!(cible instanceof HTMLInputElement)) return;
        if (TYPES_EXCLUS.has(cible.type)) return;
        if (cible.dataset.sansMajuscules !== undefined) return;

        const valeur = cible.value;
        const majuscules = valeur.toUpperCase();

        // Sortie immédiate quand il n'y a rien à changer. C'est aussi ce qui empêche la
        // récursion : l'événement que l'on redéclenche plus bas repasse ici, mais la valeur est
        // alors déjà en majuscules.
        if (valeur === majuscules) return;

        const debut = cible.selectionStart;
        const fin = cible.selectionEnd;

        // Passer par le setter natif plutôt que d'affecter `.value` directement : React mémorise
        // la dernière valeur qu'il a vue sur le nœud DOM et ignorerait un changement fait dans
        // son dos, ce qui laisserait son état interne en minuscules.
        const setteurNatif = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value')?.set;
        if (setteurNatif) {
            setteurNatif.call(cible, majuscules);
        } else {
            cible.value = majuscules;
        }

        // Restaure la position du curseur, sinon chaque frappe le renverrait en fin de champ et
        // rendrait toute correction en milieu de mot impossible. Uniquement si la longueur n'a
        // pas bougé : quelques caractères s'allongent en majuscules (« ß » → « SS ») et les
        // positions d'origine n'auraient alors plus de sens.
        if (debut !== null && fin !== null && majuscules.length === valeur.length) {
            cible.setSelectionRange(debut, fin);
        }

        cible.dispatchEvent(new Event('input', {bubbles: true}));
    };

    // Phase de capture : l'interception doit avoir lieu avant que React ne traite l'événement,
    // pour qu'il ne voie jamais que la valeur en majuscules.
    document.addEventListener('input', surSaisie, true);
    return () => document.removeEventListener('input', surSaisie, true);
}
